import { getLocale, t, useI18n } from '../../i18n';
import React, { useEffect, useState } from 'react';
import {
  FiActivity,
  FiAlertTriangle,
  FiClock,
  FiCompass,
  FiMap,
  FiRadio,
  FiRefreshCw,
  FiShield,
  FiTarget,
  FiTrendingUp,
  FiUsers,
} from 'react-icons/fi';
import {
  analyticsService,
  type AnalyticsCapacityPoint,
  type AnalyticsCompletionPoint,
  type AnalyticsDashboard,
  type AnalyticsDutyMixItem,
  type AnalyticsHeatmapChart,
  type AnalyticsIncidentMixItem,
  type AnalyticsSiteRiskRow,
  type AnalyticsStartPoint,
  type AnalyticsTimelineItem,
} from '../../services/analyticsService';
import { formatAbsoluteMinute } from '../../utils/siteScheduling';
import './AnalyticsPage.css';

const RANGE_OPTIONS = [7, 30, 90];

function formatPercent(value: number | null | undefined, fallback = '--'): string {
  if (value === null || value === undefined) return fallback;
  return `${Math.round(value)}%`;
}

function formatCompactNumber(value: number | null | undefined): string {
  return new Intl.NumberFormat(getLocale(), {
    notation: (value ?? 0) >= 1000 ? 'compact' : 'standard',
    maximumFractionDigits: (value ?? 0) >= 100 ? 0 : 1,
  }).format(value ?? 0);
}

function formatHours(value: number | null | undefined): string {
  if (value === null || value === undefined) return '--';
  if (value >= 100) return `${Math.round(value)}h`;
  if (value >= 10) return `${value.toFixed(0)}h`;
  return `${value.toFixed(1)}h`;
}

function formatMinutes(value: number | null | undefined): string {
  if (value === null || value === undefined) return '--';
  if (value >= 180) return `${Math.round(value / 60)}h`;
  if (value >= 60) return `${(value / 60).toFixed(1)}h`;
  return `${Math.round(value)}m`;
}

function formatRelativeTime(value: string | null | undefined): string {
  if (!value) return t('Unknown');
  const diffMinutes = Math.round((new Date(value).getTime() - Date.now()) / 60000);
  const formatter = new Intl.RelativeTimeFormat(getLocale(), { numeric: 'auto' });

  if (Math.abs(diffMinutes) < 60) return formatter.format(diffMinutes, 'minute');

  const diffHours = Math.round(diffMinutes / 60);
  if (Math.abs(diffHours) < 48) return formatter.format(diffHours, 'hour');

  const diffDays = Math.round(diffHours / 24);
  return formatter.format(diffDays, 'day');
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return t('Unknown');
  return new Date(value).toLocaleString(getLocale(), {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatSlotDate(value: string): string {
  return new Date(`${value}T00:00:00`).toLocaleDateString(getLocale(), {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function toneClass(tone: string): string {
  return `analytics-tone--${tone}`;
}

function timelineKindLabel(kind: AnalyticsTimelineItem['kind']): string {
  switch (kind) {
    case 'execution': return t('Shift');
    case 'segment': return t('Duty');
    case 'checkpoint': return t('Checkpoint');
    case 'report': return t('Report');
    case 'alarm': return t('Alarm');
    case 'departure': return t('Departure');
    case 'intervention': return t('Intervention');
    default: return t('Event');
  }
}

interface HeroMetricProps {
  icon: React.ReactNode;
  tone: string;
  label: string;
  value: string;
  detail: string;
  hint: string;
}

function HeroMetric({ icon, tone, label, value, detail, hint }: HeroMetricProps) {
  return (
    <div className={`analytics-hero-metric ${toneClass(tone)}`}>
      <div className="analytics-hero-metric__icon">{icon}</div>
      <div className="analytics-hero-metric__label">{label}</div>
      <div className="analytics-hero-metric__value">{value}</div>
      <div className="analytics-hero-metric__detail">{detail}</div>
      <div className="analytics-hero-metric__hint">{hint}</div>
    </div>
  );
}

interface InsightCardProps {
  tone: string;
  eyebrow: string;
  title: string;
  value: string;
  detail: string;
}

function InsightCard({ tone, eyebrow, title, value, detail }: InsightCardProps) {
  return (
    <div className={`analytics-insight ${toneClass(tone)}`}>
      <div className="analytics-insight__eyebrow">{eyebrow}</div>
      <div className="analytics-insight__title">{title}</div>
      <div className="analytics-insight__value">{value}</div>
      <div className="analytics-insight__detail">{detail}</div>
    </div>
  );
}

function PanelHeader({
  title,
  subtitle,
  aside,
}: {
  title: string;
  subtitle: string;
  aside?: React.ReactNode;
}) {
  return (
    <div className="analytics-panel__header">
      <div>
        <h2 className="analytics-panel__title">{title}</h2>
        <p className="analytics-panel__subtitle">{subtitle}</p>
      </div>
      {aside ? <div className="analytics-panel__aside">{aside}</div> : null}
    </div>
  );
}

function SeriesLegend({
  items,
}: {
  items: Array<{ label: string; tone: string }>;
}) {
  return (
    <div className="analytics-legend">
      {items.map((item) => (
        <div key={item.label} className="analytics-legend__item">
          <span className={`analytics-legend__swatch ${toneClass(item.tone)}`} />
          <span>{t(item.label)}</span>
        </div>
      ))}
    </div>
  );
}

function CapacityChart({ data }: { data: AnalyticsCapacityPoint[] }) {
  const width = Math.max(data.length * 46, 420);
  const height = 220;
  const paddingX = 22;
  const paddingY = 18;
  const plotHeight = height - (paddingY * 2);
  const plotWidth = width - (paddingX * 2);
  const maxValue = Math.max(
    ...data.flatMap((point) => [point.plannedHours, point.staffedHours, point.executedHours]),
    1,
  );
  const step = data.length > 1 ? plotWidth / (data.length - 1) : 0;
  const labelStep = Math.max(Math.ceil(data.length / 7), 1);

  const buildPath = (values: number[]) => values
    .map((value, index) => {
      const x = paddingX + (step * index);
      const y = height - paddingY - ((value / maxValue) * plotHeight);
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');

  const buildAreaPath = (values: number[]) => {
    const line = buildPath(values);
    const startX = paddingX;
    const endX = paddingX + (step * Math.max(values.length - 1, 0));
    const baseline = height - paddingY;
    return `${line} L ${endX} ${baseline} L ${startX} ${baseline} Z`;
  };

  const plannedValues = data.map((point) => point.plannedHours);
  const staffedValues = data.map((point) => point.staffedHours);
  const executedValues = data.map((point) => point.executedHours);

  return (
    <div className="analytics-line-chart">
      <svg viewBox={`0 0 ${width} ${height}`} className="analytics-line-chart__svg" role="img" aria-label={t('Duty hours comparison over time')}>
        <defs>
          <linearGradient id="analytics-capacity-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-primary)" stopOpacity="0.34" />
            <stop offset="100%" stopColor="var(--color-primary)" stopOpacity="0" />
          </linearGradient>
        </defs>

        {[0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = height - paddingY - (plotHeight * ratio);
          return (
            <line
              key={ratio}
              x1={paddingX}
              x2={width - paddingX}
              y1={y}
              y2={y}
              className="analytics-line-chart__grid"
            />
          );
        })}

        <path d={buildAreaPath(plannedValues)} className="analytics-line-chart__area" />
        <path d={buildPath(plannedValues)} className="analytics-line-chart__path analytics-line-chart__path--planned" />
        <path d={buildPath(staffedValues)} className="analytics-line-chart__path analytics-line-chart__path--staffed" />
        <path d={buildPath(executedValues)} className="analytics-line-chart__path analytics-line-chart__path--executed" />

        {data.map((point, index) => {
          if (index % labelStep !== 0 && index !== data.length - 1) return null;
          const x = paddingX + (step * index);
          return (
            <text key={point.key} x={x} y={height - 2} className="analytics-line-chart__label">
              {point.shortLabel}
            </text>
          );
        })}
      </svg>
      <SeriesLegend
        items={[
          { label: t('Planned'), tone: 'info' },
          { label: t('Staffed'), tone: 'success' },
          { label: t('Executed'), tone: 'warning' },
        ]}
      />
    </div>
  );
}

function StackedBars({
  data,
  stacks,
}: {
  data: Array<AnalyticsStartPoint | AnalyticsCompletionPoint>;
  stacks: Array<{ key: string; label: string; tone: string }>;
}) {
  const getStackValue = (point: AnalyticsStartPoint | AnalyticsCompletionPoint, key: string): number => (
    Number(((point as unknown as Record<string, number | string>)[key]) ?? 0)
  );

  const maxValue = Math.max(
    ...data.map((point) => stacks.reduce((total, stack) => total + getStackValue(point, stack.key), 0)),
    1,
  );
  const labelStep = Math.max(Math.ceil(data.length / 7), 1);

  return (
    <div className="analytics-stack-chart">
      <div className="analytics-stack-chart__track">
        {data.map((point, index) => {
          const total = stacks.reduce((sum, stack) => sum + getStackValue(point, stack.key), 0);
          const height = total ? Math.max((total / maxValue) * 100, 8) : 0;

          return (
            <div key={point.key} className="analytics-stack-chart__column">
              <div className="analytics-stack-chart__bar" style={{ height: `${height}%` }}>
                {stacks.map((stack) => {
                  const value = getStackValue(point, stack.key);
                  const ratio = total ? (value / total) * 100 : 0;
                  return ratio > 0 ? (
                    <span
                      key={stack.key}
                      className={`analytics-stack-chart__segment ${toneClass(stack.tone)}`}
                      style={{ height: `${ratio}%` }}
                    />
                  ) : null;
                })}
              </div>
              <div className="analytics-stack-chart__caption">
                {index % labelStep === 0 || index === data.length - 1 ? point.shortLabel : ''}
              </div>
            </div>
          );
        })}
      </div>
      <SeriesLegend items={stacks.map((stack) => ({ label: stack.label, tone: stack.tone }))} />
    </div>
  );
}

function DutyMixChart({ items }: { items: AnalyticsDutyMixItem[] }) {
  const maxPlanned = Math.max(...items.map((item) => item.plannedHours), 1);

  return (
    <div className="analytics-bar-list">
      {items.map((item) => {
        const plannedWidth = Math.max((item.plannedHours / maxPlanned) * 100, item.plannedHours > 0 ? 12 : 0);
        const staffedWidth = item.plannedHours > 0 ? (item.staffedHours / item.plannedHours) * 100 : 0;

        return (
          <div key={item.key} className="analytics-bar-list__row">
            <div className="analytics-bar-list__meta">
              <div className="analytics-bar-list__label">{t(item.label)}</div>
              <div className="analytics-bar-list__detail">
                {t('{staffed} staffed of {planned} planned', {
                  staffed: formatHours(item.staffedHours),
                  planned: formatHours(item.plannedHours),
                })}
              </div>
            </div>
            <div className="analytics-bar-list__track">
              <div className="analytics-bar-list__planned" style={{ width: `${plannedWidth}%` }}>
                <div className="analytics-bar-list__staffed" style={{ width: `${staffedWidth}%` }} />
              </div>
            </div>
            <div className="analytics-bar-list__value">{formatPercent(item.fillRate)}</div>
          </div>
        );
      })}
    </div>
  );
}

function IncidentMixChart({ items }: { items: AnalyticsIncidentMixItem[] }) {
  const maxCount = Math.max(...items.map((item) => item.count), 1);

  return (
    <div className="analytics-incidents-list">
      {items.map((item) => (
        <div key={item.key} className="analytics-incidents-list__row">
          <div className="analytics-incidents-list__label">{t(item.label)}</div>
          <div className="analytics-incidents-list__track">
            <div
              className={`analytics-incidents-list__fill ${toneClass(item.tone)}`}
              style={{ width: `${(item.count / maxCount) * 100}%` }}
            />
          </div>
          <div className="analytics-incidents-list__value">{formatCompactNumber(item.count)}</div>
        </div>
      ))}
    </div>
  );
}

function Heatmap({ chart }: { chart: AnalyticsHeatmapChart }) {
  return (
    <div className="analytics-heatmap">
      <div className="analytics-heatmap__header">
        <div />
        {chart.columns.map((column) => (
          <div key={column} className="analytics-heatmap__column-label">{column}</div>
        ))}
      </div>
      {chart.rows.map((row) => (
        <div key={row.key} className="analytics-heatmap__row">
          <div className="analytics-heatmap__row-label">{row.label}</div>
          {row.cells.map((cell) => (
            <div key={cell.key} className="analytics-heatmap__cell-wrap">
              <div
                className="analytics-heatmap__cell"
                style={{ opacity: Math.max(cell.intensity, 0.18) }}
                title={`${row.label} ${cell.label}: ${formatHours(cell.hours)}`}
              />
              <span className="analytics-heatmap__cell-value">{cell.hours >= 0.25 ? cell.hours.toFixed(1) : ''}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function QualityMeter({
  label,
  value,
  detail,
}: {
  label: string;
  value: number | null | undefined;
  detail: string;
}) {
  return (
    <div className="analytics-meter">
      <div className="analytics-meter__top">
        <span>{label}</span>
        <span>{formatPercent(value)}</span>
      </div>
      <div className="analytics-meter__rail">
        <div className="analytics-meter__fill" style={{ width: `${Math.max(Math.min(value ?? 0, 100), 0)}%` }} />
      </div>
      <div className="analytics-meter__detail">{detail}</div>
    </div>
  );
}

function RiskTable({ rows }: { rows: AnalyticsSiteRiskRow[] }) {
  return (
    <div className="analytics-table-wrap">
      <table className="table analytics-table">
        <thead>
          <tr>
            <th>{t('Site')}</th>
            <th>{t('Next 14d fill')}</th>
            <th>{t('Open positions')}</th>
            <th>{t('Incidents')}</th>
            <th>{t('Missed duties')}</th>
            <th>{t('Flagged traces')}</th>
            <th>{t('Risk')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.siteId ?? row.siteName}>
              <td className="analytics-table__site">
                <strong>{row.siteName}</strong>
                <span>{t('{executed} executed of {planned} planned', {
                  executed: formatHours(row.executedHours),
                  planned: formatHours(row.plannedHours),
                })}</span>
              </td>
              <td>{formatPercent(row.upcomingFillRate)}</td>
              <td>{formatCompactNumber(row.upcomingOpenPositions)}</td>
              <td>{formatCompactNumber(row.openReports + row.activeAlarms)}</td>
              <td>{formatCompactNumber(row.missedSegments)}</td>
              <td>{formatCompactNumber(row.flaggedLocations)}</td>
              <td><span className={`analytics-pill analytics-pill--${row.riskTone}`}>{t(row.riskLabel)}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return <div className="analytics-empty">{children}</div>;
}

function Timeline({ items }: { items: AnalyticsTimelineItem[] }) {
  if (!items.length) return <EmptyState>{t('No recent execution or incident events landed in the last 48 hours.')}</EmptyState>;

  return (
    <div className="analytics-timeline">
      {items.map((item) => (
        <div key={item.id} className="analytics-timeline__item">
          <div className={`analytics-timeline__dot ${toneClass(item.tone)}`} />
          <div className="analytics-timeline__body">
            <div className="analytics-timeline__top">
              <span className="analytics-timeline__title">{item.title}</span>
              <span className="analytics-timeline__time">{formatRelativeTime(item.occurredAt)}</span>
            </div>
            <div className="analytics-timeline__detail">{item.detail}</div>
            <div className="analytics-timeline__meta">
              <span>{timelineKindLabel(item.kind)}</span>
              <span>{formatDateTime(item.occurredAt)}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AnalyticsPage() {
  useI18n();
  const [rangeDays, setRangeDays] = useState(30);
  const [selectedSiteId, setSelectedSiteId] = useState('all');
  const [reloadKey, setReloadKey] = useState(0);
  const [dashboard, setDashboard] = useState<AnalyticsDashboard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    async function loadDashboard() {
      setIsLoading(true);
      setError('');

      try {
        const data = await analyticsService.fetchDashboard({
          rangeDays,
          siteId: selectedSiteId === 'all' ? null : selectedSiteId,
          cacheBust: reloadKey,
        });
        if (active) setDashboard(data);
      } catch (err) {
        if (active) setError((err as Error).message || t('Could not load analytics.'));
      } finally {
        if (active) setIsLoading(false);
      }
    }

    loadDashboard();
    return () => {
      active = false;
    };
  }, [rangeDays, reloadKey, selectedSiteId]);

  useEffect(() => {
    if (!dashboard) return;
    if (selectedSiteId === 'all') return;
    if (dashboard.availableSites.some((site) => site.id === selectedSiteId)) return;
    setSelectedSiteId('all');
  }, [dashboard, selectedSiteId]);

  if (isLoading && !dashboard) {
    return <div className="loading"><div className="spinner" /></div>;
  }

  if (!dashboard) {
    return (
      <div className="analytics-page page--full">
        <div className="msg--error">{error || t('Analytics data is unavailable.')}</div>
      </div>
    );
  }

  const incidentPressure = dashboard.incidents.openReports + dashboard.incidents.activeAlarms;
  const isSiteScoped = Boolean(dashboard.selectedSiteId);
  const scopedSiteName = dashboard.selectedSiteName || t('Selected site');
  const busiestSite = [...dashboard.riskBoard].sort((left, right) => right.plannedHours - left.plannedHours)[0] ?? null;
  const riskiestSite = dashboard.riskBoard[0] ?? null;
  const standoutGuard = dashboard.guardLeaders[0] ?? null;
  const nextGap = dashboard.upcomingGaps[0] ?? null;

  const heroMetrics: HeroMetricProps[] = [
    {
      icon: <FiUsers />,
      tone: 'info',
      label: t('Duty Coverage'),
      value: formatPercent(dashboard.quality.fillRate),
      detail: t('{staffed} staffed of {planned} planned', {
        staffed: formatHours(dashboard.headline.staffedDutyHours),
        planned: formatHours(dashboard.headline.plannedDutyHours),
      }),
      hint: t('{count} open positions in the next 14 days', {
        count: formatCompactNumber(dashboard.headline.upcomingOpenPositions),
      }),
    },
    {
      icon: <FiClock />,
      tone: 'success',
      label: t('Execution Starts'),
      value: formatPercent(dashboard.quality.startRate),
      detail: t('{started}/{due} published shifts started', {
        started: dashboard.headline.startedShifts,
        due: dashboard.headline.duePublishedShifts,
      }),
      hint: t('{count} late starts recorded', {
        count: formatCompactNumber(dashboard.headline.lateStarts),
      }),
    },
    {
      icon: <FiTarget />,
      tone: 'warning',
      label: t('Duty Delivery'),
      value: formatPercent(dashboard.quality.dutyCompletionRate),
      detail: t('{completed} completed vs {lost} lost', {
        completed: formatCompactNumber(dashboard.headline.completedDutySegments),
        lost: formatCompactNumber(dashboard.headline.missedDutySegments + dashboard.headline.skippedDutySegments),
      }),
      hint: t('{hours} executed in the selected window', {
        hours: formatHours(dashboard.headline.executedHours),
      }),
    },
    {
      icon: <FiAlertTriangle />,
      tone: 'danger',
      label: t('Incidents Open'),
      value: formatCompactNumber(incidentPressure),
      detail: t('{reports} open reports and {alarms} active alarms', {
        reports: dashboard.incidents.openReports,
        alarms: dashboard.incidents.activeAlarms,
      }),
      hint: t('{count} escalated reports still open', {
        count: dashboard.incidents.escalatedReports,
      }),
    },
    {
      icon: <FiShield />,
      tone: 'success',
      label: t('Checkpoint Proof'),
      value: formatPercent(dashboard.quality.checkpointCompletionRate),
      detail: t('{completed}/{planned} checkpoints completed', {
        completed: dashboard.headline.checkpointCompleted,
        planned: dashboard.headline.checkpointPlanned,
      }),
      hint: t('{time} average alarm resolve time', {
        time: formatMinutes(dashboard.incidents.averageAlarmResolveMinutes),
      }),
    },
    {
      icon: <FiRadio />,
      tone: 'info',
      label: t('Live Tracking'),
      value: formatPercent(dashboard.quality.geofenceCompliance),
      detail: isSiteScoped
        ? t('{guards} live-tracked guards on {site}', {
          guards: dashboard.live.liveTrackedGuards,
          site: scopedSiteName,
        })
        : t('{guards} live-tracked guards across {sites} active sites', {
          guards: dashboard.live.liveTrackedGuards,
          sites: dashboard.live.activeSites,
        }),
      hint: t('{battery} low battery - {traces} flagged traces', {
        battery: dashboard.live.lowBatteryCount,
        traces: dashboard.headline.flaggedLocations,
      }),
    },
  ];

  return (
    <div className="analytics-page page--full">
      <section className="analytics-hero">
        <div className="analytics-hero__top">
          <div>
            <div className="analytics-hero__eyebrow">{isSiteScoped ? t('Site Focus') : t('Duties Intelligence')}</div>
            <h1 className="page__title analytics-hero__title">{isSiteScoped ? scopedSiteName : dashboard.companyName}</h1>
            <p className="analytics-hero__subtitle">
              {isSiteScoped
                ? t('Operational analytics for {site} within {company}, built on the current duties-based model.', {
                  site: scopedSiteName,
                  company: dashboard.companyName,
                })
                : t('Live duty coverage, execution quality, incident pressure, and roster risk built on the current duties-based model.')}
            </p>
          </div>
          <div className="analytics-hero__actions">
            <div className="analytics-site-filter">
              <label className="analytics-site-filter__label" htmlFor="analytics-site-filter">{t('Site')}</label>
              <select
                id="analytics-site-filter"
                className="analytics-site-filter__control"
                value={selectedSiteId}
                onChange={(event) => setSelectedSiteId(event.target.value)}
              >
                <option value="all">{t('All sites')}</option>
                {dashboard.availableSites.map((site) => (
                  <option key={site.id} value={site.id}>{site.name}</option>
                ))}
              </select>
            </div>
            <div className="analytics-range-tabs">
              {RANGE_OPTIONS.map((days) => (
                <button
                  key={days}
                  type="button"
                  className={`analytics-range-tabs__button${rangeDays === days ? ' analytics-range-tabs__button--active' : ''}`}
                  onClick={() => setRangeDays(days)}
                >
                  {days}D
                </button>
              ))}
            </div>
            <button type="button" className="btn btn--secondary analytics-refresh" onClick={() => setReloadKey((value) => value + 1)}>
              <FiRefreshCw />
              <span>{isLoading ? t('Refreshing') : t('Refresh')}</span>
            </button>
          </div>
        </div>

        <div className="analytics-meta">
          <span>{dashboard.rangeLabel}</span>
          <span>{isSiteScoped ? scopedSiteName : t('All sites')}</span>
          <span>{t('Updated {time}', { time: formatRelativeTime(dashboard.generatedAt) })}</span>
        </div>

        <div className="analytics-hero-grid">
          {heroMetrics.map((metric) => (
            <HeroMetric key={metric.label} {...metric} />
          ))}
        </div>
      </section>

      {error ? <div className="msg--error">{error}</div> : null}
      {dashboard.warnings.length ? <div className="analytics-warning">{dashboard.warnings.join(' | ')}</div> : null}

      <div className="analytics-insights">
        <InsightCard
          tone="info"
          eyebrow={isSiteScoped ? t('Selected Site') : t('Busiest Site')}
          title={busiestSite?.siteName || (isSiteScoped ? scopedSiteName : t('No activity'))}
          value={busiestSite ? formatHours(busiestSite.plannedHours) : '--'}
          detail={busiestSite
            ? t('{hours} executed in window', { hours: formatHours(busiestSite.executedHours) })
            : t('No planned duty hours in this range.')}
        />
        <InsightCard
          tone={riskiestSite?.riskTone || 'neutral'}
          eyebrow={t('Exposure')}
          title={riskiestSite?.siteName || (isSiteScoped ? scopedSiteName : t('Stable'))}
          value={riskiestSite ? t('{count} open', { count: formatCompactNumber(riskiestSite.upcomingOpenPositions) }) : '--'}
          detail={riskiestSite
            ? t('{incidents} live incidents - {fill} next-14d fill', {
              incidents: riskiestSite.openReports + riskiestSite.activeAlarms,
              fill: formatPercent(riskiestSite.upcomingFillRate),
            })
            : t('No site risk signals are active.')}
        />
        <InsightCard
          tone="success"
          eyebrow={t('Guard Momentum')}
          title={standoutGuard?.fullName || t('No standout')}
          value={standoutGuard ? formatHours(standoutGuard.executedHours) : '--'}
          detail={standoutGuard
            ? t('{punctuality} punctual - {completion} duty completion', {
              punctuality: formatPercent(standoutGuard.punctualityRate),
              completion: formatPercent(standoutGuard.dutyCompletionRate),
            })
            : t('No recent execution activity to score.')}
        />
        <InsightCard
          tone="warning"
          eyebrow={t('Next Gap')}
          title={nextGap?.dutyName || t('No gaps queued')}
          value={nextGap ? formatSlotDate(nextGap.slotDate) : '--'}
          detail={nextGap
            ? t('{site} - {count} open at {time}', {
              site: isSiteScoped ? scopedSiteName : nextGap.siteName,
              count: nextGap.openPositions || 0,
              time: formatAbsoluteMinute(nextGap.startMinute),
            })
            : t('The next 14 days are fully staffed and published.')}
        />
      </div>

      <div className="analytics-main-grid">
        <section className="analytics-panel">
          <PanelHeader
            title={t('Capacity Graph')}
            subtitle={t('Planned, staffed, and actually executed duty hours across the selected window.')}
            aside={<span className="analytics-chip">{t('Hours')}</span>}
          />
          <div className="analytics-panel__stats">
            <div><strong>{formatHours(dashboard.headline.plannedDutyHours)}</strong><span>{t('Planned')}</span></div>
            <div><strong>{formatHours(dashboard.headline.staffedDutyHours)}</strong><span>{t('Staffed')}</span></div>
            <div><strong>{formatHours(dashboard.headline.executedHours)}</strong><span>{t('Executed')}</span></div>
          </div>
          <CapacityChart data={dashboard.charts.capacity} />
        </section>

        <section className="analytics-panel">
          <PanelHeader
            title={t('Incident Overview')}
            subtitle={t('How the active incident queue is split right now.')}
            aside={<span className="analytics-chip analytics-chip--danger">{t('{count} live items', { count: formatCompactNumber(incidentPressure) })}</span>}
          />
          <IncidentMixChart items={dashboard.incidents.statusMix} />
          <div className="analytics-mini-grid">
            <div className="analytics-mini-stat"><span>{t('Average time before review')}</span><strong>{formatHours(dashboard.incidents.averageOpenReportAgeHours)}</strong></div>
            <div className="analytics-mini-stat"><span>{t('Report close time')}</span><strong>{formatHours(dashboard.incidents.averageReportCloseHours)}</strong></div>
            <div className="analytics-mini-stat"><span>{t('Alarm resolve')}</span><strong>{formatMinutes(dashboard.incidents.averageAlarmResolveMinutes)}</strong></div>
            <div className="analytics-mini-stat"><span>{t('Escalated')}</span><strong>{formatCompactNumber(dashboard.incidents.escalatedReports)}</strong></div>
          </div>
        </section>

        <section className="analytics-panel">
          <PanelHeader
            title={t('Punctuality Trend')}
            subtitle={t('Starts and duty outcomes day by day.')}
          />
          <div className="analytics-subchart">
            <div className="analytics-subchart__label">{t('Shift starts')}</div>
            <StackedBars
              data={dashboard.charts.starts}
              stacks={[
                { key: 'onTimeStarts', label: 'On time', tone: 'success' },
                { key: 'lateStarts', label: 'Late', tone: 'warning' },
                { key: 'unstartedShifts', label: 'No start', tone: 'danger' },
              ]}
            />
          </div>
          <div className="analytics-subchart">
            <div className="analytics-subchart__label">{t('Duty outcomes')}</div>
            <StackedBars
              data={dashboard.charts.completions}
              stacks={[
                { key: 'completedSegments', label: 'Completed', tone: 'success' },
                { key: 'missedSegments', label: 'Missed', tone: 'danger' },
                { key: 'skippedSegments', label: 'Skipped', tone: 'warning' },
              ]}
            />
          </div>
        </section>

        <section className="analytics-panel">
          <PanelHeader
            title={t('Duty Mix')}
            subtitle={t('Where planned hours are landing and how well each duty type is staffed.')}
          />
          <DutyMixChart items={dashboard.charts.dutyMix} />
        </section>

        <section className="analytics-panel analytics-panel--full">
          <PanelHeader
            title={t('Roster Load Map')}
            subtitle={t('Average planned guard-hours per weekday and four-hour block.')}
            aside={<span className="analytics-chip">{t('Avg hrs / day block')}</span>}
          />
          <Heatmap chart={dashboard.charts.heatmap} />
        </section>
      </div>

      <div className="analytics-support-grid">
        <section className="analytics-panel">
          <PanelHeader
            title={t('Quality Metrics')}
            subtitle={t('The operating ratios worth watching every day.')}
          />
          <div className="analytics-meters">
            <QualityMeter
              label={t('Duty fill')}
              value={dashboard.quality.fillRate}
              detail={t('{covered} of {planned} covered', {
                covered: formatHours(dashboard.headline.staffedDutyHours),
                planned: formatHours(dashboard.headline.plannedDutyHours),
              })}
            />
            <QualityMeter
              label={t('Publication')}
              value={dashboard.quality.publicationRate}
              detail={t('{count} upcoming positions still exposed', {
                count: formatCompactNumber(dashboard.headline.upcomingOpenPositions),
              })}
            />
            <QualityMeter
              label={t('Start rate')}
              value={dashboard.quality.startRate}
              detail={t('{started}/{due} due shifts started', {
                started: dashboard.headline.startedShifts,
                due: dashboard.headline.duePublishedShifts,
              })}
            />
            <QualityMeter
              label={t('Punctuality')}
              value={dashboard.quality.punctualityRate}
              detail={t('{count} late starts in window', {
                count: formatCompactNumber(dashboard.headline.lateStarts),
              })}
            />
            <QualityMeter
              label={t('Duty completion')}
              value={dashboard.quality.dutyCompletionRate}
              detail={t('{count} duties missed or skipped', {
                count: formatCompactNumber(dashboard.headline.missedDutySegments + dashboard.headline.skippedDutySegments),
              })}
            />
            <QualityMeter
              label={t('Checkpoint completion')}
              value={dashboard.quality.checkpointCompletionRate}
              detail={t('{completed}/{planned} planned checkpoints hit', {
                completed: dashboard.headline.checkpointCompleted,
                planned: dashboard.headline.checkpointPlanned,
              })}
            />
            <QualityMeter
              label={t('Report ownership')}
              value={dashboard.quality.reportAssignmentRate}
              detail={t('{count} reports still open', { count: dashboard.incidents.openReports })}
            />
            <QualityMeter
              label={t('Geofence compliance')}
              value={dashboard.quality.geofenceCompliance}
              detail={t('{count} flagged location samples', { count: dashboard.headline.flaggedLocations })}
            />
          </div>
        </section>

        <section className="analytics-panel">
          <PanelHeader
            title={t('Live Watch')}
            subtitle={t('Current operational posture from executions and telemetry.')}
          />
          <div className="analytics-watch-grid">
            <div className="analytics-watch-card"><FiActivity /><strong>{formatCompactNumber(dashboard.live.activeExecutions)}</strong><span>{t('Active executions')}</span></div>
            <div className="analytics-watch-card"><FiMap /><strong>{formatCompactNumber(dashboard.live.activeSites)}</strong><span>{t('Sites currently active')}</span></div>
            <div className="analytics-watch-card"><FiTrendingUp /><strong>{formatCompactNumber(dashboard.live.overrunningExecutions)}</strong><span>{t('Overrunning shifts')}</span></div>
            <div className="analytics-watch-card"><FiCompass /><strong>{formatCompactNumber(dashboard.live.liveTrackedGuards)}</strong><span>{t('Live-tracked guards')}</span></div>
            <div className="analytics-watch-card"><FiAlertTriangle /><strong>{formatCompactNumber(dashboard.live.lowBatteryCount)}</strong><span>{t('Low battery devices')}</span></div>
            <div className="analytics-watch-card"><FiShield /><strong>{formatCompactNumber(dashboard.live.activeSiteDepartures)}</strong><span>{t('Active departures')}</span></div>
            <div className="analytics-watch-card"><FiRadio /><strong>{formatCompactNumber(dashboard.live.manualInterventions)}</strong><span>{t('Manual interventions')}</span></div>
          </div>
        </section>
      </div>

      <section className="analytics-panel analytics-panel--table">
        <PanelHeader
          title={t('Site Risk Board')}
          subtitle={isSiteScoped
            ? t('Coverage exposure and execution drag for {site}.', { site: scopedSiteName })
            : t('Coverage exposure and execution drag rolled up by site.')}
        />
        <RiskTable rows={dashboard.riskBoard} />
      </section>

      <div className="analytics-bottom-grid">
        <section className="analytics-panel">
          <PanelHeader
            title={t('Guard Momentum')}
            subtitle={t('Who is carrying the most operational load and with what reliability.')}
          />
          {dashboard.guardLeaders.length ? (
            <div className="analytics-list">
              {dashboard.guardLeaders.map((guard) => (
                <div key={guard.guardId} className="analytics-list__item">
                  <div className="analytics-list__main">
                    <div className="analytics-list__title">{guard.fullName}</div>
                    <div className="analytics-list__meta">
                      {t('{hours} executed - {punctuality} punctual - {delivery} delivery', {
                        hours: formatHours(guard.executedHours),
                        punctuality: formatPercent(guard.punctualityRate),
                        delivery: formatPercent(guard.dutyCompletionRate),
                      })}
                    </div>
                  </div>
                  <div className="analytics-list__badges">
                    {guard.liveTracked ? <span className="analytics-pill analytics-pill--info">{t('Live')}</span> : null}
                    {guard.activeExecution ? <span className="analytics-pill analytics-pill--success">{t('Active')}</span> : null}
                  </div>
                </div>
              ))}
            </div>
          ) : <EmptyState>{t('No guard activity landed in this range.')}</EmptyState>}
        </section>

        <section className="analytics-panel">
          <PanelHeader
            title={t('Operations Timeline')}
            subtitle={t('Recent execution, incident, and intervention signals.')}
          />
          <Timeline items={dashboard.timeline} />
        </section>
      </div>
    </div>
  );
}
